import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/use-page-meta";
import { 
  Calendar, 
  Clock, 
  Users, 
  Bell, 
  BarChart3, 
  Globe, 
  CreditCard, 
  Star,
  Smartphone,
  MapPin,
  Check,
  FileText,
  Mail,
  Headphones,
  Building,
  DoorOpen,
  ClipboardList,
  PartyPopper,
  Banknote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { ReviewsMarquee } from "@/components/landing/ReviewsMarquee";
import { ProductPhoneMarquee } from "@/components/landing/ProductPhoneMarquee";
import { MobileAppsSection } from "@/components/landing/MobileAppsSection";
import { useAuth } from "@/contexts/AuthContext";
import { captureException } from "@/lib/sentry";
import { detectPublicMarket } from "@/lib/public-market";
import { isNativeIosApp } from "@/lib/platform";

// Small helper that only renders in production when a specific query param is present.
// Use https://buukd.co/?sentry-test=1 to show the button and trigger a test error for Sentry.
const SentryTestButton = () => {
  if (import.meta.env.MODE !== "production") return null;

  if (typeof window === "undefined") return null;
  const search = window.location.search || "";
  if (!search.includes("sentry-test=1")) return null;

  return (
    <button
      type="button"
      onClick={() => {
        const error = new Error("Buukd Sentry test error");
        // Send directly to Sentry so we don't depend on global handlers
        captureException(error, { test: true, source: "sentry-setup" });
        // Still throw so you see it in the console (optional)
        throw error;
      }}
      className="mt-4 text-xs text-muted-foreground underline underline-offset-2"
    >
      Trigger Sentry test error
    </button>
  );
};

const solutions = [
  {
    id: "appointments",
    icon: Calendar,
    title: "Appointments",
    tagline: "Let clients book you online 24/7 instead of chasing messages all day.",
    bestFor: ["Salons & barbers", "Clinics", "Spas", "Studios", "Mentorship"],
    includes: [
      "Public booking page that takes bookings 24/7",
      "Clients pick a time, reschedule, and get reminders without you calling",
      "One calendar instead of WhatsApp chats, notebooks, and spreadsheets",
      "Visit and payment history so you can see loyal clients and no‑shows",
      "Simple email/SMS campaigns to fill slow days and bring past clients back",
    ],
    image: "/Gemini_Generated_Image_3btpud3btpud3btp.png",
    imageAlt: "Salon and barber — book and manage appointments in one place",
  },
  {
    id: "memberships",
    icon: DoorOpen,
    title: "Memberships + Access Desk",
    tagline: "Membership management and walk-in check‑ins",
    bestFor: ["Gyms", "Sports clubs", "Wellness centers", "Community clubs"],
    includes: [
      "Membership plans with clear start and end dates",
      "Member IDs (e.g. TSC‑000123) so check‑ins are fast at the door",
      "Check-in/out log instead of paper notebooks and loose sheets",
      "Day passes & tickets for drop‑ins and events",
      "Targeted messages to members when you launch new classes, offers, or events",
    ],
    image: "/Gemini_Generated_Image_wufhnawufhnawufh.png",
    imageAlt: "Gym and fitness — memberships and access desk",
  },
  {
    id: "catering_services",
    icon: ClipboardList,
    title: "Catering Services",
    tagline: "Share your menu online so orders and event requests don’t get lost in calls and chats.",
    bestFor: ["Food vendors", "Corporate caterers", "Meal prep vendors", "Office lunch services"],
    includes: [
      "Public orders page with cart that runs 24/7 for staff and customers",
      "Weekly menu builder and company links so HR shares once and you get clean orders",
      "Orders dashboard that groups submissions for easy kitchen prep (no more scrolling chats)",
      "Event / buffet request form so every detail is captured in one place instead of scattered messages",
      "One shareable link for social, WhatsApp, and email campaigns when you want to run promos",
    ],
    image: "/Gemini_Generated_Image_6y1fbu6y1fbu6y1f.png",
    imageAlt: "Catering and food services — orders and client experience",
  },
];

const features = [
  {
    icon: Calendar,
    title: "One platform, multiple modules",
    description: "Choose one primary module during signup, and keep growing as your business evolves.",
  },
  {
    icon: Users,
    title: "Shared customer database",
    description: "Appointments customers and membership members live in the same customer list — one profile, multiple services.",
  },
  {
    icon: DoorOpen,
    title: "Access Desk (POS-style)",
    description: "Search members, check them in/out, sell day passes, and print tickets — mobile-friendly for staff.",
  },
  {
    icon: ClipboardList,
    title: "Orders for corporate catering",
    description:
      "Create weekly menus, generate company links, accept public orders, and manage event / buffet requests from a single dashboard.",
  },
  {
    icon: PartyPopper,
    title: "Events & registrations",
    description: "Publish events with public URLs, capacity controls, and email ticket confirmations (QR included).",
  },
  {
    icon: FileText,
    title: "Invoicing for B2B",
    description: "Create and send invoices to businesses, with taxes/discounts support and a public invoice page.",
  },
  {
    icon: Bell,
    title: "In-app notifications",
    description: "Get real-time alerts for new bookings, buffet requests, and order submissions — with unread badges.",
  },
  {
    icon: MapPin,
    title: "Google Maps location tools",
    description: "Search addresses, pick on map, and open locations in Google Maps for events and catering requests.",
  },
  {
    icon: CreditCard,
    title: "Payments ready",
    description: "Collect deposits and accept local payment methods — and expand to more payment flows over time.",
  },
  {
    icon: BarChart3,
    title: "Reports that match your module",
    description: "Analytics adapts to your business type: bookings, visits, orders, feedback, and revenue trends.",
  },
  {
    icon: Smartphone,
    title: "Mobile-first UI",
    description: "Modern, responsive pages and consistent modals designed to work well on phones.",
  },
  {
    icon: Building,
    title: "Built for teams",
    description: "Operate across staff and locations with a clean admin dashboard and secure access controls.",
  },
  {
    icon: Banknote,
    title: "Log Book",
    description: "Track daily activities and expenses with a calendar-based log book. Record line items, manage costs, and keep detailed records of your business operations.",
  },
  {
    icon: Star,
    title: "Service Reviews",
    description:
      "Collect reviews and quick feedback automatically after completion, with shareable links you can add to emails and booking pages to build trust.",
  },
];

const quickSetupSteps = [
  {
    id: "1",
    title: "Create your profile",
    description:
      "Set up your business, add services with durations and prices, and customize your booking page to match your brand.",
  },
  {
    id: "2",
    title: "Share your booking link",
    description:
      "Add your Buukd booking link to Instagram, WhatsApp, Google, and your website so clients can book 24/7.",
  },
  {
    id: "3",
    title: "Focus on your clients",
    description:
      "Buukd handles reminders, confirmations, and payment flows while you focus on delivering great service.",
  },
];

export default function Index() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const location = useLocation();
  const publicMarket = detectPublicMarket();
  const regionalMessage = publicMarket.regionLabel
    ? `Built for service businesses in ${publicMarket.regionLabel} and beyond.`
    : "Built for service businesses across Africa, the UK, and the USA.";

  usePageMeta({
    title: "Buukd – Booking & Food Ordering | Ghana, Nigeria, Africa, UK, USA",
    description:
      "Appointment booking and food ordering for service businesses. Book and order online 24/7 in Ghana, Nigeria, South Africa, UK, USA. Free trial.",
    path: "/",
  });

  // If the user already has an active session, take them straight into the app.
  // This prevents the "I have to sign in again" feeling when reopening the PWA at /.
  useEffect(() => {
    if (!loading && user) {
      navigate("/app", { replace: true });
    }
  }, [loading, user, navigate]);

  // Make hash links like /#solutions reliably scroll in a SPA.
  // (React Router won't always auto-scroll to anchors.)
  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    // Small delay so layout is settled (header is fixed)
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }, [location.hash]);

  const isNativeIos = isNativeIosApp();

  return (
    <div className="min-h-screen flex flex-col font-bricolage">
      <PublicHeader />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 bg-gradient-hero relative overflow-hidden">
          <div className="absolute inset-0 bg-dotted opacity-50" />
          <div className="container-app relative">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
              <div className="max-w-2xl order-1 text-center lg:text-left">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                  <span className="bg-gradient-to-r from-sky-500 via-violet-500 to-pink-500 bg-clip-text text-transparent animate-gradient-subtle">
                    The #1 app
                  </span>
                  <br />
                  for beauty, wellness, &amp; fitness
                </h1>
                <p className="mt-6 text-lg text-muted-foreground">
                  Buukd keeps your bookings, visits, orders, and payments in one place so you spend less time replying messages and more time serving customers.
                </p>
                <p className="mt-3 text-sm text-muted-foreground">
                  {regionalMessage}
                </p>
                <div className="mt-8 flex flex-row items-center lg:items-start justify-center lg:justify-start gap-3">
                  {!isNativeIos && (
                    <Button asChild variant="gradient" size="pill-lg">
                      <Link to="/signup">Start 7 day Free Trial</Link>
                    </Button>
                  )}
                  <Button asChild variant="outline" size="pill-lg">
                    <Link to="/signin">Sign in</Link>
                  </Button>
                </div>
                {isNativeIos && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Access is provided by your organization administrator.
                  </p>
                )}
                <SentryTestButton />
              </div>
              <div className="order-2 lg:order-2 flex justify-center">
                {/* iPhone outline — full-length screen */}
                <div className="relative w-[280px] sm:w-[300px] mx-auto">
                  <div className="rounded-[2.5rem] border-[10px] border-foreground/90 bg-foreground/90 shadow-2xl overflow-hidden" style={{ boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)" }}>
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-5 sm:w-32 sm:h-6 bg-foreground/90 rounded-b-2xl z-10" />
                    {/* Screen area — full-length aspect (iPhone portrait) */}
                    <div className="relative aspect-[9/19] min-h-[480px] sm:min-h-[520px] bg-background overflow-hidden">
                      <img
                        src="/IMG_0957.PNG"
                        alt="Buukd app — appointments, bookings, and dashboard"
                        className="absolute inset-0 w-full h-full object-cover object-top"
                        onError={(e) => {
                          const t = e.target as HTMLImageElement;
                          t.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <MobileAppsSection />

        {/* Simple setup */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container-app">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-pink-500">
                Simple Setup
              </p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground leading-tight">
                Up and running in under 10 minutes
              </h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
                No technical setup required. Buukd is built for busy service teams, not engineering teams.
              </p>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {quickSetupSteps.map((step) => (
                <article
                  key={step.id}
                  className="bg-card rounded-2xl border border-border p-6 shadow-soft text-center"
                >
                  <div className="mx-auto h-12 w-12 rounded-full bg-pink-500 text-white font-bold text-xl flex items-center justify-center shadow-soft-md">
                    {step.id}
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-foreground">{step.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Appointment reminders and communication */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container-app">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground">
                Effortless scheduling for you and your clients
              </h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
                Buukd automates reminders, confirmations, and booking updates so your team stays organized and clients always know what is next.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <article className="lg:row-span-2 rounded-3xl border border-border bg-card overflow-hidden shadow-soft">
                <div className="h-72 bg-gradient-to-br from-indigo-400/35 via-cyan-300/25 to-sky-200/35 p-6 flex items-center justify-center">
                  <div className="w-[190px] rounded-[2rem] border-[7px] border-white/90 bg-white/85 shadow-xl p-3">
                    <div className="rounded-xl bg-muted p-3 text-left">
                      <p className="text-xs text-muted-foreground">Tue, 4:30 PM</p>
                      <p className="mt-2 text-xs font-medium text-foreground">Kendra - Hair Treatment</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">Auto reminder sent 24h before</p>
                    </div>
                    <div className="mt-3 rounded-lg border border-border bg-background px-2 py-1.5 text-[11px] text-foreground">
                      <span className="font-semibold">Client:</span> Confirmed
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold text-foreground leading-tight">
                    Never miss a booking with 24/7 online booking
                  </h3>
                  <p className="mt-3 text-muted-foreground">
                    Clients book anytime from your Buukd page, then get confirmation and reminder updates automatically.
                  </p>
                </div>
              </article>

              <article className="lg:col-span-2 rounded-3xl border border-border bg-card overflow-hidden shadow-soft">
                <div className="grid md:grid-cols-5">
                  <div className="md:col-span-2 bg-gradient-to-br from-sky-100 via-indigo-100 to-pink-100 p-6 flex items-center justify-center">
                    <div className="relative w-full max-w-[230px] aspect-square rounded-3xl border border-white/70 bg-white/75 shadow-lg">
                      <div className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white bg-white shadow flex items-center justify-center">
                        <img
                          src={`${import.meta.env.BASE_URL || "/"}pwa-512x512.png`}
                          alt="Buukd"
                          className="h-10 w-10 rounded-xl object-cover"
                        />
                      </div>
                      <div className="absolute left-6 top-6 h-12 w-12 rounded-full bg-white border border-border shadow flex items-center justify-center overflow-hidden">
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
                          alt="Google"
                          className="h-8 w-8 object-contain"
                          loading="lazy"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="absolute right-6 top-6 h-12 w-12 rounded-full bg-white border border-border shadow flex items-center justify-center overflow-hidden">
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png"
                          alt="Instagram"
                          className="h-8 w-8 object-contain"
                          loading="lazy"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="absolute left-5 bottom-7 h-12 w-12 rounded-full bg-white border border-border shadow flex items-center justify-center overflow-hidden">
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg"
                          alt="Facebook"
                          className="h-8 w-8 object-contain"
                          loading="lazy"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="absolute right-5 bottom-7 h-12 w-12 rounded-full bg-white border border-border shadow flex items-center justify-center overflow-hidden">
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                          alt="WhatsApp"
                          className="h-8 w-8 object-contain"
                          loading="lazy"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="absolute left-1/2 top-4 h-10 w-10 -translate-x-1/2 rounded-full bg-white border border-border shadow flex items-center justify-center overflow-hidden">
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg"
                          alt="Apple Maps"
                          className="h-6 w-6 object-contain"
                          loading="lazy"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="absolute left-1/2 bottom-4 h-10 w-10 -translate-x-1/2 rounded-full bg-white border border-border shadow flex items-center justify-center overflow-hidden">
                        <img
                          src="https://upload.wikimedia.org/wikipedia/en/a/a9/TikTok_logo.svg"
                          alt="TikTok"
                          className="h-6 w-6 object-contain"
                          loading="lazy"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = "none";
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-3 p-6">
                    <h3 className="text-2xl font-semibold text-foreground leading-tight">
                      Booking buttons everywhere your clients are
                    </h3>
                    <p className="mt-3 text-muted-foreground">
                      Get booked from your website, Google profile, maps, social media, and direct messages with one Buukd booking flow.
                    </p>
                    <p className="mt-4 text-sm text-pink-600 font-medium">Get discovered and booked faster</p>
                  </div>
                </div>
              </article>

              <article className="lg:col-span-2 rounded-3xl border border-border bg-card overflow-hidden shadow-soft">
                <div className="grid md:grid-cols-5">
                  <div className="md:col-span-2 bg-gradient-to-br from-cyan-500/25 via-indigo-500/20 to-blue-500/20 p-6 flex items-center justify-center">
                    <div className="w-full max-w-[220px] rounded-xl border border-border bg-white px-4 py-3 shadow-lg">
                      <button type="button" className="w-full rounded-lg bg-rose-500 text-white font-semibold py-2 text-sm">
                        Book Now
                      </button>
                      <p className="mt-2 text-[11px] text-muted-foreground">Embed on your website or bio link</p>
                    </div>
                  </div>
                  <div className="md:col-span-3 p-6">
                    <h3 className="text-2xl font-semibold text-foreground leading-tight">
                      Expand your reach with a booking widget on your website
                    </h3>
                    <p className="mt-3 text-muted-foreground">
                      Add Buukd booking links to your website, Instagram, TikTok, and WhatsApp so clients can book in one tap.
                    </p>
                    <p className="mt-4 text-sm text-pink-600 font-medium">Drive more direct bookings</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Reminder outcomes */}
        <section className="py-16 lg:py-24 bg-subtle-gray">
          <div className="container-app">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground">
                Reduce no-shows with reminders and confirmations
              </h2>
              <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
                Keep your calendar moving with proactive communication before every appointment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <article className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <div className="min-h-[300px] rounded-2xl bg-gradient-to-br from-indigo-500/30 via-violet-400/25 to-sky-300/35 p-3 sm:p-4 flex items-start sm:items-center">
                  <div className="w-full max-w-[320px] mx-auto rounded-[22px] border border-white/60 bg-white/90 shadow-xl overflow-hidden">
                    <div className="px-3 py-2 border-b border-border/70 bg-slate-50/90 text-[11px] text-muted-foreground flex items-center justify-between">
                      <span>Messages</span>
                      <span>Now</span>
                    </div>
                    <div className="p-3 space-y-2.5">
                      <div className="flex items-start gap-2">
                        <img
                          src={`${import.meta.env.BASE_URL || "/"}pwa-512x512.png`}
                          alt="Buukd"
                          className="h-6 w-6 rounded-md object-cover shrink-0 mt-0.5"
                        />
                        <div className="min-w-0">
                          <p className="text-[11px] text-muted-foreground">Buukd SMS</p>
                          <div className="mt-1 rounded-2xl rounded-tl-sm bg-[#e5e5ea] px-3 py-2 text-[11px] text-foreground leading-relaxed">
                            Glow Studio: Booking confirmed.
                            <br />
                            Service: Deep Tissue Massage
                            <br />
                            When: 12 Oct 2026, 16:30
                            <br />
                            Code: K9M2QH
                            <br />
                            Manage:{" "}
                            <a
                              href="https://buukd.co/book/glowstudio/manage?code=K9M2QH"
                              className="text-blue-600 underline break-all"
                            >
                              buukd.co/book/glowstudio/manage?code=K9M2QH
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="max-w-[88%] rounded-2xl rounded-tr-sm bg-[#34c759] text-white px-3 py-2 text-[11px] leading-relaxed">
                          Got it, thank you.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="mt-5 text-2xl font-semibold text-foreground">Free automated reminders</h3>
                <p className="mt-2 text-muted-foreground">
                  Send timely reminders by email and SMS, then let clients confirm without staff follow-ups.
                </p>
                <p className="mt-3 text-sm text-pink-600 font-medium">Keep your schedule full</p>
              </article>

              <article className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <div className="min-h-[300px] rounded-2xl bg-gradient-to-br from-slate-500/35 via-zinc-500/30 to-cyan-500/25 p-3 sm:p-4 flex items-start sm:items-center">
                  <div className="w-full max-w-[320px] mx-auto rounded-[22px] border border-white/60 bg-white/90 shadow-xl overflow-hidden">
                    <div className="px-3 py-2 border-b border-border/70 bg-slate-50/90 text-[11px] text-muted-foreground flex items-center justify-between">
                      <span>Messages</span>
                      <span>2m</span>
                    </div>
                    <div className="p-3 space-y-2.5">
                      <div className="flex items-start gap-2">
                        <img
                          src={`${import.meta.env.BASE_URL || "/"}pwa-512x512.png`}
                          alt="Buukd"
                          className="h-6 w-6 rounded-md object-cover shrink-0 mt-0.5"
                        />
                        <div className="min-w-0">
                          <p className="text-[11px] text-muted-foreground">Buukd SMS</p>
                          <div className="mt-1 rounded-2xl rounded-tl-sm bg-[#e5e5ea] px-3 py-2 text-[11px] text-foreground leading-relaxed">
                            Reminder (24h): Your appointment at Glow Studio is in 24 hours. Deep Tissue Massage - 13 Oct 2026, 16:30.
                          </div>
                          <div className="mt-2 rounded-2xl rounded-tl-sm bg-[#e5e5ea] px-3 py-2 text-[11px] text-foreground leading-relaxed">
                            Reminder (2h): Your appointment at Glow Studio is in 2 hours. Deep Tissue Massage - 13 Oct 2026, 16:30.
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <div className="max-w-[88%] rounded-2xl rounded-tr-sm bg-[#34c759] text-white px-3 py-2 text-[11px] leading-relaxed">
                          Thanks for the reminder.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="mt-5 text-2xl font-semibold text-foreground">No-stress client confirmations</h3>
                <p className="mt-2 text-muted-foreground">
                  Prompt confirmations improve attendance and give your team confidence that clients will show up.
                </p>
                <p className="mt-3 text-sm text-pink-600 font-medium">Start sending confirmations</p>
              </article>
            </div>
          </div>
        </section>

        {/* Journey notifications */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container-app">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="rounded-3xl border border-border bg-gradient-to-br from-sky-500/30 via-indigo-500/25 to-violet-500/35 p-4 sm:p-6 shadow-soft min-h-[280px] sm:min-h-[320px] flex items-center">
                <div className="w-full space-y-3 sm:space-y-4">
                  <div className="rounded-2xl border border-white/40 bg-white/75 px-3 py-3 sm:px-4 sm:py-3.5 text-sm">
                    <div className="grid grid-cols-[auto_1fr_auto] items-start gap-2 sm:gap-3">
                      <img
                        src={`${import.meta.env.BASE_URL || "/"}pwa-512x512.png`}
                        alt="Buukd"
                        className="h-7 w-7 rounded-lg object-cover shrink-0"
                      />
                      <span className="font-medium text-foreground leading-snug break-words">
                        Ama Mensah moved booking code K9M2QH to 4:30 PM
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0 pt-0.5">Now</span>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/40 bg-white/75 px-3 py-3 sm:px-4 sm:py-3.5 text-sm">
                    <div className="grid grid-cols-[auto_1fr_auto] items-start gap-2 sm:gap-3">
                      <img
                        src={`${import.meta.env.BASE_URL || "/"}pwa-512x512.png`}
                        alt="Buukd"
                        className="h-7 w-7 rounded-lg object-cover shrink-0"
                      />
                      <span className="font-medium text-foreground leading-snug break-words">
                        Ama Mensah updated customer details for booking code T4R8XN
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0 pt-0.5">2m</span>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/40 bg-white/75 px-3 py-3 sm:px-4 sm:py-3.5 text-sm">
                    <div className="grid grid-cols-[auto_1fr_auto] items-start gap-2 sm:gap-3">
                      <img
                        src={`${import.meta.env.BASE_URL || "/"}pwa-512x512.png`}
                        alt="Buukd"
                        className="h-7 w-7 rounded-lg object-cover shrink-0"
                      />
                      <span className="font-medium text-foreground leading-snug break-words">
                        Ama Mensah rescheduled booking code P7W3LY to Friday 2:00 PM
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0 pt-0.5">5m</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
                  Notifications at every step of the journey
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  Guide customers from booking to checkout with clear updates that elevate the full Buukd experience.
                </p>
                <div className="mt-6 flex flex-wrap gap-2 text-sm">
                  <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5">
                    <Check className="h-4 w-4 text-pink-500" />
                    Booking confirmations
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5">
                    <Check className="h-4 w-4 text-pink-500" />
                    Reminder schedules
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5">
                    <Check className="h-4 w-4 text-pink-500" />
                    Invoice and order alerts
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Marketing and analytics */}
        <section className="py-16 lg:py-24 bg-subtle-gray">
          <div className="container-app">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground">
                Marketing that brings customers back
              </h2>
              <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
                Run email and SMS campaigns from Buukd, then track results from one analytics view to see what drives bookings.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <article className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <div className="h-44 rounded-2xl border border-border bg-gradient-to-br from-pink-100 via-rose-100 to-purple-100 p-4">
                  <div className="rounded-xl bg-white/90 border border-border p-3 shadow-sm">
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>Email Campaign</span>
                      <span>Sent</span>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-foreground">Weekend Self-Care Offer</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Subject: 15% off bookings this weekend
                    </p>
                    <div className="mt-3 text-xs text-muted-foreground">Recipients: 1,240 customers</div>
                  </div>
                </div>
                <h3 className="mt-5 text-xl font-semibold text-foreground">Email campaigns</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Send branded offers, announcements, and seasonal promos to past and active customers.
                </p>
                <p className="mt-3 text-sm text-pink-600 font-medium">Keep your business top of mind</p>
              </article>

              <article className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <div className="h-44 rounded-2xl border border-border bg-gradient-to-br from-sky-100 via-cyan-100 to-indigo-100 p-4">
                  <div className="rounded-xl bg-white/90 border border-border p-3 shadow-sm">
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>SMS Campaign</span>
                      <span>Live</span>
                    </div>
                    <p className="mt-2 text-sm text-foreground">
                      Glow Studio: Flash offer - 10% off tomorrow only. Book now with code Q7R2MP.
                    </p>
                    <div className="mt-3 text-xs text-muted-foreground">Delivered: 892 of 910 contacts</div>
                  </div>
                </div>
                <h3 className="mt-5 text-xl font-semibold text-foreground">SMS campaigns</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Reach clients instantly with time-sensitive promotions, reminders, and booking links.
                </p>
                <p className="mt-3 text-sm text-pink-600 font-medium">Drive immediate response</p>
              </article>

              <article className="rounded-3xl border border-border bg-card p-5 shadow-soft">
                <div className="h-44 rounded-2xl border border-border bg-gradient-to-br from-emerald-100 via-teal-100 to-cyan-100 p-4">
                  <div className="h-full rounded-xl bg-white/90 border border-border p-3 shadow-sm flex flex-col justify-between">
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>Business Analytics</span>
                      <BarChart3 className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Revenue</span>
                        <span className="font-medium text-foreground">GHS 24,860</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Appointments</span>
                        <span className="font-medium text-foreground">312</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Repeat clients</span>
                        <span className="font-medium text-foreground">64%</span>
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="mt-5 text-xl font-semibold text-foreground">Business analytics</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  See revenue, booking volume, and repeat-customer trends so you can make better day-to-day business decisions.
                </p>
                <p className="mt-3 text-sm text-pink-600 font-medium">Track growth with confidence</p>
              </article>
            </div>
          </div>
        </section>

        {/* Product screens in iPhone outline — slow marquee */}
        <section className="py-16 lg:py-24 bg-subtle-gray relative overflow-hidden">
          <div className="container-app relative mb-10">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-foreground">
                Built to run day-to-day operations
              </h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
                Whether you schedule services, manage memberships, or collect weekly orders — Buukd keeps everything organized in one place.
              </p>
            </div>
          </div>
          <ProductPhoneMarquee />
        </section>

        {/* Reviews marquee */}
        <section className="py-14 lg:py-20 bg-background relative overflow-hidden">
          <div className="container-app relative mb-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-foreground">What people say about Buukd</h2>
              <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
                Real stories from business owners who switched to one platform for bookings, payments, and orders.
              </p>
            </div>
          </div>
          <ReviewsMarquee />
        </section>

        {/* Features Section */}
        <section className="py-16 lg:py-24 bg-subtle-gray relative overflow-hidden">
          <div className="absolute inset-0 bg-dotted opacity-30" />
          <div className="container-app relative">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground">Our Features</h2>
              <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
                One platform with the tools you need — from booking and payments to memberships and orders.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="bg-card rounded-xl border border-border p-6 shadow-soft hover:shadow-soft-md transition-shadow"
                >
                  <div className="h-12 w-12 rounded-lg bg-pink-500/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-pink-500" />
                  </div>
                  <h3 className="font-bold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container-app">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-foreground">
                Ready to streamline your business?
              </h2>
              <p className="mt-4 text-muted-foreground">
                Start with one module and expand as you grow — bookings, memberships, orders, events, and invoicing.
              </p>
              <div className="mt-8 flex flex-row items-center justify-center gap-3">
                {!isNativeIos && (
                  <Button asChild variant="gradient" size="pill-lg">
                    <Link to="/signup">Get Started</Link>
                  </Button>
                )}
                <Button asChild variant="outline" size="pill-lg">
                  <Link to="/signin">Sign in</Link>
                </Button>
              </div>
              {isNativeIos && (
                <p className="mt-3 text-xs text-muted-foreground">
                  New organizations are onboarded outside the iOS app.
                </p>
              )}
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
